import { useMemo, useRef, useState } from 'react'
import { CheckboxIcon, CloseIcon, FilterIcon, SearchIcon } from '../icons/icons'
import { FieldSelectorTrigger } from './FieldSelectorTrigger'
import Popover from '@features/shared/components/Popover'
import { Tooltip } from './Tooltip'
import PopoverScrollArea from './PopoverScrollArea'
import { useSelectorSearchFocus } from '@features/shared/components/useSelectorSearchFocus'
import { SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS } from './ValueSelector'
import { MENU_OPTION_LIST_CLASS } from '@features/shared/components/compactDropdownStyles'
import { useFieldDensity } from './fieldDensity'

export interface MultiValueSelectorOption {
  id: string
  label: string
  /** Shorter label for the selected chip ("Feb"); falls back to `label`. */
  chipLabel?: string
  /** Faint right-aligned helper in the menu row. */
  hint?: string
  /** Optional text used for filtering in addition to the visible label. */
  searchText?: string
  lockedSelected?: boolean
  lockReason?: string
  /** Disables selecting this option; selected options stay removable. */
  disabled?: boolean
  disabledReason?: string
}

interface MultiValueSelectorProps {
  options: MultiValueSelectorOption[]
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  emptyOptionsMessage?: string
  disabled?: boolean
  ariaLabel: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  onOpen?: () => void
  ariaDescribedBy?: string
  onBlur?: () => void
  controlClassName?: string
  /**
   * Explicit search control. Defaults by option count: search appears only
   * when the list has at least `SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS` entries.
   */
  showSearch?: boolean
  /** Chip remove-button label; defaults to the permission-domain wording. */
  chipRemoveLabel?: (label: string) => string
  /** Tooltip on directly selected chips. */
  chipTitle?: string
  /**
   * The selection's order matters (join keys matched by position): chips
   * follow `values` with their position, a new pick goes last, and dragging a
   * chip reorders it.
   */
  ordered?: boolean
}

export function MultiValueSelector({
  options,
  values,
  onChange,
  placeholder = 'None',
  emptyOptionsMessage = 'No options are available',
  disabled = false,
  ariaLabel,
  ariaInvalid,
  ariaRequired,
  onOpen,
  ariaDescribedBy,
  onBlur,
  controlClassName,
  chipRemoveLabel,
  chipTitle = 'Direct membership',
  showSearch,
  ordered = false,
}: MultiValueSelectorProps) {
  const searchEnabled = showSearch ?? options.length >= SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS
  const chipHeightClass = useFieldDensity() === 'compact' ? 'h-5' : 'h-[22px]'
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const selectedValueIds = useMemo(() => new Set(values), [values])
  // While a chip is dragged, the order it would land in; committed on release.
  const [dragOrder, setDragOrder] = useState<string[] | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const reorderable = ordered && !disabled
  const selectedOptions = ordered
    ? (dragOrder ?? values).flatMap((id) => options.find((option) => option.id === id) ?? [])
    : options.filter((option) => selectedValueIds.has(option.id) || option.lockedSelected)
  const moveDragged = (overId: string, after: boolean) => {
    if (draggedId === null || draggedId === overId) return
    const current = dragOrder ?? values
    const order = current.filter((id) => id !== draggedId)
    const index = order.indexOf(overId)
    if (index < 0) return
    order.splice(after ? index + 1 : index, 0, draggedId)
    if (order.every((id, position) => current[position] === id)) return
    setDragOrder(order)
  }
  const finishDrag = () => {
    if (dragOrder && dragOrder.some((id, position) => values[position] !== id)) {
      onChange(dragOrder)
    }
    setDraggedId(null)
    setDragOrder(null)
  }
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase()
    return options
      .filter((option) => (option.searchText ?? option.label).toLocaleLowerCase().includes(term))
      .sort((a, b) => Number(Boolean(a.disabled)) - Number(Boolean(b.disabled)))
  }, [options, search])

  const toggleOption = (optionId: string) => {
    if (ordered) {
      // Kept in order; a new pick goes last.
      onChange(
        values.includes(optionId) ? values.filter((id) => id !== optionId) : [...values, optionId]
      )
    } else {
      const nextValues = new Set(values)
      if (nextValues.has(optionId)) nextValues.delete(optionId)
      else nextValues.add(optionId)
      onChange(options.filter((option) => nextValues.has(option.id)).map((option) => option.id))
    }
    searchInputRef.current?.focus()
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return
    if (nextOpen && !open) onOpen?.()
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }

  useSelectorSearchFocus({ open, enabled: searchEnabled, inputRef: searchInputRef })

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      align="left"
      showCaret={false}
      triggerMode="manual"
      matchTriggerWidth
      fitContentWidthSelector="[data-multi-value-selector-option]"
      sideOffsetPx={4}
      wrapperClassName="block w-full"
      triggerClassName="w-full"
      contentClassName="flex max-h-72 min-h-0 flex-col overflow-hidden p-0"
      trigger={
        <FieldSelectorTrigger
          open={open}
          onOpenChange={handleOpenChange}
          disabled={disabled}
          ariaLabel={ariaLabel}
          ariaInvalid={ariaInvalid}
          ariaRequired={ariaRequired}
          ariaDescribedBy={ariaDescribedBy}
          onBlur={onBlur}
          className={controlClassName}
        >
          {selectedOptions.length > 0 ? (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              {selectedOptions.map((option, index) => {
                const directlySelected = selectedValueIds.has(option.id)
                return directlySelected ? (
                  <Tooltip
                    key={option.id}
                    variant="label"
                    content={
                      ordered
                        ? `${index + 1}. ${option.label}${reorderable ? ' (drag to reorder)' : ''}`
                        : option.lockedSelected
                          ? 'Also assigned by filter'
                          : chipTitle
                    }
                    className="min-w-0 max-w-[150px] shrink-0"
                  >
                    <span
                      className={`inline-flex ${chipHeightClass} min-w-0 max-w-[150px] items-center gap-1 rounded border border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] px-1.5 text-[11px] leading-none text-[color:var(--accent-info)] ${
                        disabled
                          ? ''
                          : 'transition-colors hover:border-[color:var(--accent-info)] hover:text-[color:var(--accent-info-marker)]'
                      } ${reorderable ? 'cursor-grab' : ''} ${draggedId === option.id ? 'opacity-50' : ''}`}
                      draggable={reorderable || undefined}
                      // A press on a chip that can be dragged waits to see
                      // whether it drags; a plain click still opens the list.
                      onPointerDown={reorderable ? (event) => event.stopPropagation() : undefined}
                      onClick={
                        reorderable
                          ? (event) => {
                              event.stopPropagation()
                              handleOpenChange(!open)
                            }
                          : undefined
                      }
                      onDragStart={
                        reorderable
                          ? (event) => {
                              event.dataTransfer.effectAllowed = 'move'
                              event.dataTransfer.setData('text/plain', option.label)
                              setDraggedId(option.id)
                              setDragOrder(values)
                            }
                          : undefined
                      }
                      onDragOver={
                        reorderable && draggedId !== null
                          ? (event) => {
                              event.preventDefault()
                              event.dataTransfer.dropEffect = 'move'
                              const box = event.currentTarget.getBoundingClientRect()
                              moveDragged(option.id, event.clientX > box.left + box.width / 2)
                            }
                          : undefined
                      }
                      onDrop={reorderable ? (event) => event.preventDefault() : undefined}
                      onDragEnd={reorderable ? finishDrag : undefined}
                    >
                      {ordered ? (
                        <span className="shrink-0 text-[9px] font-medium tabular-nums text-muted">
                          {index + 1}
                        </span>
                      ) : null}
                      {option.lockedSelected ? <FilterIcon size={10} aria-hidden="true" /> : null}
                      <span className="truncate">{option.chipLabel ?? option.label}</span>
                      {disabled ? null : (
                        <button
                          type="button"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation()
                            toggleOption(option.id)
                          }}
                          aria-label={
                            chipRemoveLabel
                              ? chipRemoveLabel(option.label)
                              : `Remove direct membership in ${option.label}`
                          }
                          className="inline-flex shrink-0 items-center justify-center border-0 bg-transparent p-0 text-icon-quiet transition-colors hover:text-subtle focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                        >
                          <CloseIcon size={9} aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    key={option.id}
                    variant="label"
                    content={option.lockReason}
                    className="min-w-0 max-w-[150px] shrink-0"
                  >
                    <span
                      className={`inline-flex ${chipHeightClass} min-w-0 max-w-[150px] items-center gap-1 rounded border border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] px-1.5 text-[11px] leading-none text-[color:var(--accent-info)]`}
                    >
                      <FilterIcon size={10} aria-hidden="true" />
                      <span className="truncate">{option.chipLabel ?? option.label}</span>
                    </span>
                  </Tooltip>
                )
              })}
            </div>
          ) : (
            <span className="truncate font-light italic text-muted [font-synthesis:style]">
              {placeholder}
            </span>
          )}
        </FieldSelectorTrigger>
      }
    >
      {searchEnabled ? (
        <div className="relative flex-none border-b border-muted">
          <SearchIcon
            size={14}
            className="pointer-events-none absolute left-[9px] top-1/2 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={`Search ${ariaLabel}`}
            placeholder="Search..."
            className="h-10 w-full border-0 surface-default pl-8 pr-4 text-sm text-default outline-none transition-colors placeholder:font-light placeholder:italic placeholder:text-muted focus:outline-none"
          />
        </div>
      ) : null}
      <PopoverScrollArea
        role="listbox"
        aria-multiselectable="true"
        className="min-h-0 flex-1"
        // No outer padding here, so the track needs no gutter.
        scrollbarGutterClassName=""
        style={{ maxHeight: 240 }}
      >
        <div className={`p-1 ${MENU_OPTION_LIST_CLASS}`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const directlySelected = selectedValueIds.has(option.id)
              // A disabled option stays removable when already selected.
              const optionDisabled = disabled || (Boolean(option.disabled) && !directlySelected)
              const optionButton = (
                <button
                  key={option.id}
                  data-multi-value-selector-option="true"
                  type="button"
                  role="option"
                  aria-selected={directlySelected}
                  aria-disabled={optionDisabled || undefined}
                  aria-description={optionDisabled ? option.disabledReason : undefined}
                  onClick={() => {
                    if (!optionDisabled) toggleOption(option.id)
                  }}
                  className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm text-default transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${optionDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted'}`}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    <CheckboxIcon size={14} checked={directlySelected} />
                  </span>
                  <span data-popover-fit-content-label="true" className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {option.hint ? (
                    <span
                      aria-hidden="true"
                      className="ml-auto shrink-0 pl-4 text-[11px] tabular-nums text-muted"
                    >
                      {option.hint}
                    </span>
                  ) : null}
                  {option.lockedSelected ? (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-muted">
                      <FilterIcon size={11} aria-hidden="true" /> Filter
                    </span>
                  ) : null}
                </button>
              )
              // An option that can't be added says why; one the filter assigns says so.
              const lockReason =
                option.lockedSelected && !(option.disabled && !directlySelected)
                  ? option.lockReason
                  : undefined
              return optionDisabled && option.disabledReason ? (
                <Tooltip key={option.id} content={option.disabledReason} fullWidth>
                  {optionButton}
                </Tooltip>
              ) : lockReason ? (
                <Tooltip key={option.id} variant="label" content={lockReason} fullWidth>
                  {optionButton}
                </Tooltip>
              ) : (
                optionButton
              )
            })
          ) : (
            <div className="px-2 py-2 text-sm italic text-muted">
              {options.length === 0 ? emptyOptionsMessage : 'No matching values'}
            </div>
          )}
        </div>
      </PopoverScrollArea>
    </Popover>
  )
}
